import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import ChatMessage, { StatusEvent } from './ChatMessage';
import ChatInput from './ChatInput';
import { api } from '../utils/api';
import {
  statusLabel,
  searchQuery,
  searchResultSummary,
  FIRST_STEP_LABEL,
  THINKING_LABEL,
} from '../utils/agentStatus';

const API_URL = window.APP_CONFIG?.apiUrl || window.location.origin;

interface Message {
  id?: number;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  statusEvents?: StatusEvent[];
}

interface Conversation {
  id: number;
  title: string;
  created_at: string;
}

interface ChatInterfaceProps {
  conversationId?: number;
  onConversationCreated?: (id: number) => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ conversationId: initialConversationId, onConversationCreated }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationId, setConversationId] = useState<number | null>(initialConversationId || null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Load existing conversation only on initial mount
  // If mounted without a conversationId (new chat), never load — even if prop changes later
  const initialIdOnMount = useRef(initialConversationId);
  useEffect(() => {
    if (!initialIdOnMount.current) return; // Mounted as new chat — never load from DB

    const loadConversation = async () => {
      setLoading(true);
      try {
        const data = await api.get(`/api/chat/conversations/${initialIdOnMount.current}`);
        if (data.success && data.messages) {
          setMessages(data.messages.map((m: any) => ({
            id: m.id,
            role: m.role,
            content: m.content,
          })));
          setConversationId(initialIdOnMount.current!);
        }
      } catch (error) {
        console.error('Error loading conversation:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  const handleSend = async (text: string) => {
    // Create conversation if needed
    let activeConversationId: number | null = conversationId;
    if (!activeConversationId) {
      try {
        const data = await api.post('/api/chat/conversations', {
          title: text.length > 60 ? text.substring(0, 57) + '...' : text,
        });
        if (data.success) {
          activeConversationId = data.conversation.id as number;
          setConversationId(activeConversationId);
          onConversationCreated?.(activeConversationId!);
        } else {
          console.error('Failed to create conversation');
          return;
        }
      } catch (error) {
        console.error('Error creating conversation:', error);
        return;
      }
    }

    // Add user message to UI
    const userMessage: Message = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);

    // Add placeholder assistant message
    const assistantMessage: Message = {
      role: 'assistant',
      content: '',
      isStreaming: true,
      statusEvents: [{ type: 'client', label: FIRST_STEP_LABEL }],
    };
    setMessages(prev => [...prev, assistantMessage]);
    setIsStreaming(true);

    try {
      // Stream the response via SSE
      const response = await fetch(`${API_URL}/api/chat/conversations/${activeConversationId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const updateAssistantMessage = (updates: Partial<Message>) => {
        setMessages(prev => {
          if (prev.length === 0) return prev;
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last && last.role === 'assistant') {
            updated[updated.length - 1] = { ...last, ...updates, isStreaming: updates.isStreaming ?? true };
          }
          return updated;
        });
      };

      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedText = '';

      // Two kinds of progress, shown differently:
      //  - evidence: what the agent searched for and what it found. Accumulates, because
      //    it is the substance of the wait and stays useful once superseded.
      //  - activity: the orchestration phase it is in right now. Replaces itself, because
      //    it cycles (plan -> search -> review -> plan) several times per answer.
      let evidence: StatusEvent[] = [];
      let activity: StatusEvent | null = { type: 'client', label: FIRST_STEP_LABEL };
      const MAX_EVIDENCE = 3;
      const STALL_MS = 6000;
      let stallTimer: ReturnType<typeof setTimeout> | null = null;
      let finished = false;

      const render = () => {
        const visible = [...evidence.slice(-MAX_EVIDENCE)];
        if (activity) visible.push(activity);
        updateAssistantMessage({ statusEvents: visible });
      };

      const armStallTimer = () => {
        if (stallTimer) clearTimeout(stallTimer);
        stallTimer = setTimeout(() => {
          if (finished || !activity || activity.stalled) return;
          activity = { ...activity, stalled: true };
          render();
        }, STALL_MS);
      };

      // Phases can arrive several in the same millisecond; pace them so the line is
      // readable. Purely cosmetic — content deltas bypass this and clear it outright.
      const MIN_ACTIVITY_MS = 700;
      let activityShownAt = 0;
      let pendingActivity: string | null = null;
      let activityTimer: ReturnType<typeof setTimeout> | null = null;

      const applyActivity = (label: string) => {
        activity = { type: 'agent', label };
        activityShownAt = Date.now();
        pendingActivity = null;
        render();
        armStallTimer();
      };

      /** Current phase — replaces whatever phase was showing. */
      const setActivity = (label: string | null) => {
        if (!label || finished || activity?.label === label) return;
        const elapsed = Date.now() - activityShownAt;
        if (elapsed >= MIN_ACTIVITY_MS) {
          applyActivity(label);
          return;
        }
        // Keep only the most recent — intermediate phases in a burst are skipped
        pendingActivity = label;
        if (!activityTimer) {
          activityTimer = setTimeout(() => {
            activityTimer = null;
            if (pendingActivity && !finished) applyActivity(pendingActivity);
          }, MIN_ACTIVITY_MS - elapsed);
        }
      };

      /** A durable fact about the search — appended to the list. */
      const addEvidence = (label: string, detail?: string) => {
        if (finished) return;
        const last = evidence[evidence.length - 1];
        if (last && last.label === label && last.detail === detail) return;
        evidence.push({ type: 'evidence', label, detail, done: true });
        render();
        armStallTimer();
      };

      const clearSteps = () => {
        finished = true;
        if (stallTimer) { clearTimeout(stallTimer); stallTimer = null; }
        if (activityTimer) { clearTimeout(activityTimer); activityTimer = null; }
        pendingActivity = null;
        evidence = [];
        activity = null;
      };

      armStallTimer();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const eventStr of events) {
          const lines = eventStr.trim().split('\n');
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const data = line.slice(6).trim();
            if (data === '[DONE]') continue;

            try {
              const event = JSON.parse(data);

              // The answer has started — progress display has served its purpose
              if (event.type === 'response.text.delta' && event.text) {
                accumulatedText += event.text;
                clearSteps();
                updateAssistantMessage({ content: accumulatedText, statusEvents: [] });
                continue;
              }

              // Thinking is internal monologue — acknowledge it, never show it
              if (event.type === 'response.thinking.delta' || event.type === 'response.thinking') {
                setActivity(THINKING_LABEL);
                continue;
              }

              if (event.type === 'response.status' || event.type === 'response.tool_result.status') {
                setActivity(statusLabel(event.status, event.message));
                continue;
              }

              if (event.type === 'cortex_search') {
                const query = searchQuery(event);
                if (query) {
                  addEvidence('Searched for', `\u201c${query}\u201d`);
                  setActivity(statusLabel('executing_tool'));
                  continue;
                }
                const summary = searchResultSummary(event);
                if (summary) addEvidence(summary);
                continue;
              }

              if (event.type === 'error') {
                accumulatedText += `\n\n**Error:** ${event.error || 'An error occurred'}`;
                clearSteps();
                updateAssistantMessage({ content: accumulatedText, isStreaming: false, statusEvents: [] });
              }
            } catch {
              // Skip unparseable events
            }
          }
        }
      }

      clearSteps();

      // Finalize the assistant message
      updateAssistantMessage({
        content: accumulatedText || 'No response received.',
        isStreaming: false,
        statusEvents: [],
      });
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => {
        if (prev.length === 0) return prev;
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last && last.role === 'assistant') {
          updated[updated.length - 1] = {
            ...last,
            content: 'Sorry, an error occurred while processing your request. Please try again.',
            isStreaming: false,
            statusEvents: [],
          };
        }
        return updated;
      });
    } finally {
      setIsStreaming(false);
    }

  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: '#ffffff',
      borderRadius: 2,
      overflow: 'hidden',
      border: '1px solid rgba(0,0,0,0.1)',
    }}>
      {/* Messages area */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        px: 2,
        py: 2,
      }}>
        {messages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Medical Records Analyst
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
              Ask questions about the medical records retrieved for your case.
              The analyst will search through the records and provide sourced answers.
            </Typography>
          </Box>
        )}

        {messages.map((msg, index) => (
          <ChatMessage
            key={index}
            role={msg.role}
            content={msg.content}
            isStreaming={msg.isStreaming}
            statusEvents={msg.statusEvents}
          />
        ))}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input area */}
      <ChatInput
        onSend={handleSend}
        disabled={isStreaming}
        placeholder="Ask about your medical records..."
      />
    </Box>
  );
};

export default ChatInterface;
